// src/components/sections/Gallery.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import GalleryCard from '../ui/GalleryCard';
import { galleryImages } from '../../data/galleryImages';
import { staggerContainer, fadeInUp, scaleUp } from '../../utils/animations';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const Gallery: React.FC = () => {
  const { ref, controls } = useScrollAnimation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = ['all', ...Array.from(new Set(galleryImages.map(img => img.category)))];
  
  const filteredImages = activeCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  return (
    <section className="section-padding" id="gallery">
      <motion.div
        ref={ref}
        initial="initial"
        animate={controls}
        variants={staggerContainer}
        className="container-custom"
      >
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <h2 className="text-4xl font-bold text-mauve-900 mb-4">
            Ghanaian Nail Art Gallery
          </h2>
          <p className="text-lg text-mauve-600 max-w-2xl mx-auto">
            Explore our collection of traditional and contemporary nail designs
          </p>
        </motion.div>

        {/* Category Filters */}
        <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              className={`px-6 py-2 rounded-full font-medium transition-all capitalize ${
                activeCategory === category
                  ? 'bg-mauve-500 text-white'
                  : 'bg-mauve-100 text-mauve-700 hover:bg-mauve-200'
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image, index) => (
            <motion.div
              key={image.id}
              variants={scaleUp}
              custom={index}
            >
              <GalleryCard
                image={image}
                onClick={() => setSelectedImage(image.id)}
              />
            </motion.div>
          ))}
        </div>

        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="relative max-w-4xl w-full max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-4 text-white hover:text-mauve-300 z-10"
                  onClick={() => setSelectedImage(null)}
                >
                  <X size={32} />
                </button>
                
                {/* Image Display */}
                <div className="bg-gradient-to-br from-mauve-400 to-mauve-600 rounded-xl overflow-hidden h-full">
                  <div className="aspect-video flex items-center justify-center text-white p-8">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold mb-2">
                        {galleryImages.find(img => img.id === selectedImage)?.category} Design
                      </h3>
                      <p className="text-lg opacity-90">
                        {galleryImages.find(img => img.id === selectedImage)?.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default Gallery;